import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useCourseStore from '../stores/useCourseStore';
import DiscussionThread from '../components/DiscussionThread';
import DashboardLayout from '../components/DashboardLayout';
import useAuthStore from '../stores/useAuthStore';
import axiosClient from '../services/api';
import { useToast } from '../components/ToastContainer';

const DiscussionPage = () => {
  const { user } = useAuthStore();
  const toast = useToast();
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { course, fetchCourse, isLoading: courseLoading, error: courseError } = useCourseStore();
  
  const [threads, setThreads] = useState([]);
  const [newThread, setNewThread] = useState({
    title: '',
    content: '',
    tags: []
  });
  const [newTag, setNewTag] = useState('');
  const [isCreatingThread, setIsCreatingThread] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingCreateThread, setIsLoadingCreateThread] = useState(false);


  // Fetch course and threads
  useEffect(() => {
    fetchCourse(courseId);
    fetchThreads();
  }, [courseId, activeTab]);

  const fetchThreads = async (showLoader = true) => {
    if (showLoader) setIsLoading(true);
    try {
      const res = await axiosClient.get(`/discussions/courses/${courseId}/threads`, {
        params: { status: activeTab }
      });
      setThreads(res.data.threads);
    } catch (err) {
      toast.error('Failed to load discussions');
    } finally {
      if (showLoader) setIsLoading(false);
    }
  };

  const handleCreateThread = async (e) => {
    e.preventDefault();
    if (!newThread.title.trim() || !newThread.content.trim()) return;

    try {
      setIsLoadingCreateThread(true)
      const res = await axiosClient.post(`/discussions/courses/${courseId}/threads`, newThread);
      setThreads(prev => [res.data.thread, ...prev]);
      setNewThread({ title: '', content: '', tags: [] });
      setNewTag('');
      setIsLoadingCreateThread(false)
      setIsCreatingThread(false);
      toast.success('Thread created successfully!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create thread');
    } finally {
      setIsLoadingCreateThread(false)
    }
  };

  const handleAddTag = (e) => {
    e.preventDefault();
    if (newTag.trim() && !newThread.tags.includes(newTag.trim())) {
      setNewThread(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setNewThread(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleVote = async (targetId, voteType, targetType = 'thread') => {
    try {
      await axiosClient.post('/discussions/vote', {
        type: voteType,
        targetId,
        targetType
      });
      fetchThreads(false);
    } catch (err) {
      toast.error('Failed to update vote');
    }
  };

  const handleReply = async (threadId, content) => {
    try {
      const res = await axiosClient.post(`/discussions/threads/${threadId}/replies`, { content });
      setThreads(prev => prev.map(thread => 
        thread._id === threadId 
          ? { ...thread, replies: [res.data.reply, ...(thread.replies || [])] }
          : thread
      ));
      toast.success('Reply posted!');
    } catch (err) {
      toast.error('Failed to post reply');
    }
  };

  if (courseLoading || isLoading) {
    return (
      <DashboardLayout userRole={user.role}>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (courseError || !course) {
    return (
      <DashboardLayout userRole={user.role}>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold mb-2">Course not found</h2>
            <button onClick={() => navigate(-1)} className="text-primary-600 hover:underline">
              Go back
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userRole={user.role}>
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
        <div className="max-w-6xl mx-auto px-4">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate(-1)}
              className="text-primary-600 dark:text-primary-400 hover:underline mb-4 flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
              Back to course
            </button>
            
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">
                  Discussion: {course.title}
                </h1>
                <p className="text-neutral-600 dark:text-neutral-400 mt-2">
                  Ask questions, share insights, and collaborate with fellow students
                </p>
              </div>
              <button
                onClick={() => setIsCreatingThread(true)}
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                New Thread
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-8">
            <div className="border-b border-neutral-200 dark:border-neutral-700">
              <nav className="-mb-px flex space-x-8">
                {['all', 'resolved', 'unresolved'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                      activeTab === tab
                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Create Thread Form */}
          {isCreatingThread && (
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6 mb-8">
              <h2 className="text-xl font-semibold mb-6 dark:text-neutral-300">Create New Thread</h2>
              <form onSubmit={handleCreateThread} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2 dark:text-neutral-400">Title</label>
                  <input
                    type="text"
                    value={newThread.title}
                    onChange={(e) => setNewThread({...newThread, title: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-neutral-700 dark:text-neutral-300 border-neutral-300 dark:border-neutral-600 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 dark:text-neutral-400">Content</label>
                  <textarea
                    value={newThread.content}
                    onChange={(e) => setNewThread({...newThread, content: e.target.value})}
                    rows="6"
                    className="w-full px-3 py-2 border rounded-lg dark:bg-neutral-700 dark:text-neutral-300 border-neutral-300 dark:border-neutral-600 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 dark:text-neutral-400">Tags</label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {newThread.tags.map((tag, index) => (
                      <span key={index} className="px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-sm rounded-full flex items-center">
                        {tag}
                        <button type="button" onClick={() => handleRemoveTag(tag)} className="ml-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex">
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddTag(e)}
                      className="flex-1 px-3 py-2 border rounded-lg dark:bg-neutral-700 dark:text-neutral-300 border-neutral-300 dark:border-neutral-600 focus:ring-primary-500"
                      placeholder="Add a tag and press Enter"
                    />
                    <button
                      type="button"
                      onClick={handleAddTag}
                      className="px-4 py-2 bg-primary-500 text-white rounded-r-lg"
                    >
                      Add
                    </button>
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <button type="button" onClick={() => setIsCreatingThread(false)} className="px-4 py-2 bg-neutral-200 dark:bg-neutral-700 dark:text-neutral-300 rounded-lg">
                    Cancel
                  </button>
                  <button type="submit" disabled={isLoadingCreateThread} className={`px-4 py-2 text-white rounded-lg ${isLoadingCreateThread ? 'bg-gray-400' : 'bg-primary-500 hover:bg-primary-600'}`} >
                    {isLoadingCreateThread ? 'Creating...' : 'Create Thread'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Threads */}
          {threads?.length > 0 ? (
            <div className="space-y-6">
              {threads?.map(thread => (
                <DiscussionThread
                  key={thread?._id}
                  thread={thread}
                  onVote={handleVote}
                  onReply={handleReply}
                />
              ))}
            </div>
          ) : (
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-8 text-center">
              <div className="text-6xl mb-4">💬</div>
              <h3 className="text-xl font-semibold mb-2 dark:text-neutral-300">No discussions yet</h3>
              <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                {activeTab === 'all' ? 'Be the first to start a discussion!' : `No ${activeTab} discussions found.`}
              </p>
              {!isCreatingThread && (
                <button
                  onClick={() => setIsCreatingThread(true)}
                  className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm"
                >
                  Start a Discussion
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DiscussionPage;